import {Component, inject, Input, OnChanges, OnInit, SecurityContext, signal, SimpleChanges} from '@angular/core';
import {PermissionService} from '../../../../../core/services/permission.service';
import {SystemPower} from '../../../../../shared/model/permissions';
import {GameTable} from '../../../../../shared/model/game-table';
import {Router, RouterLink} from '@angular/router';
import {DomSanitizer} from '@angular/platform-browser';
import {UserGeolocationService} from '../../../../../shared/services/user-geolocation.service';
import {tableCoordinates} from '../../../../../shared/utils/map-markers';
import {distanceFromUserLabel} from '../../../../../shared/utils/geo-distance';
import {TruncatePipe} from '../../../../../shared/pipes/truncate.pipe';

@Component({
  selector: 'app-game-table-card',
  standalone: true,
  imports: [RouterLink, TruncatePipe],
  templateUrl: './game-table-card.component.html',
  styleUrl: './game-table-card.component.css',
})
export class GameTableCardComponent implements OnInit, OnChanges {
  @Input() gameTable?: GameTable;
  @Input() showTitle: boolean = true;
  @Input() showDescription: boolean = true;
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private userGeolocation: UserGeolocationService = inject(UserGeolocationService);
  protected isOwner: boolean = false;
  protected isAdmin: boolean = false;
  protected hasImageError = signal<boolean>(false);
  private permissionService: PermissionService = inject(PermissionService);
  private router: Router = inject(Router);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gameTable']) {
      this.hasImageError.set(false);
    }
  }

  ngOnInit(): void {
    if (this.gameTable) {
      this.isOwner = Boolean(this.gameTable.isCreator);
    }
    this.isAdmin = this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR);
  }

  protected onImageError(): void {
    this.hasImageError.set(true);
  }

  protected canEdit(): boolean {
    return this.isOwner || this.isAdmin;
  }

  protected onCardClick(event: Event): void {
    if (!this.gameTable || this.isInteractiveTarget(event)) {
      return;
    }
    void this.router.navigate(['/tables/detail', this.gameTable.id]);
  }

  protected onCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (this.isInteractiveTarget(event)) {
      return;
    }
    event.preventDefault();
    this.onCardClick(event);
  }

  protected get sanitizedDescription(): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.gameTable?.description ?? '') ?? '';
  }

  protected distanceLabel(): string {
    if (!this.gameTable) {
      return '';
    }
    return distanceFromUserLabel(this.userGeolocation.position(), tableCoordinates(this.gameTable));
  }

  private isInteractiveTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('a, button, input, select, textarea, label, [role="button"]');
  }
}
